import { debounce } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { DatePicker, SearchInput, Select } from '../../common/components';
import { futureDuration, pastDuration } from '../../constants/reports';
import { dateFormat } from '../../utils/constant';
import { getStartDateEndDate } from '../../utils/handlers';
import { DURATION } from './utils/dashboardConstants';

function DurationFilters({ type, dateChangeHandler, searchHandler }) {
  let durationOptions = pastDuration;
  let initialValues = { startDate: new Date(), endDate: new Date() };
  const singleReportDate = moment().startOf('day').toDate();
  let options = {
    minDate: '',
    maxDate: type === DURATION.SINGLE ? singleReportDate : moment().endOf('day')._d,
    dateFormat: dateFormat.M_D_Y,
  };

  const getRange = (rangeType) => {
    const range = getStartDateEndDate(rangeType);
    range.startDate = moment(range.startDate).toDate();
    range.endDate = moment(range.endDate).toDate();
    return range;
  };

  if (type === DURATION.PAST) {
    initialValues = getRange(pastDuration[2].value);
  } else if (type === DURATION.FUTURE) {
    options = {
      minDate: moment().startOf('day')._d,
      maxDate: '',
      dateFormat: dateFormat.M_D_Y,
    };
    durationOptions = futureDuration;
    initialValues = getRange(futureDuration[1].value);
  }

  const [startDate, setStartDate] = useState(initialValues?.startDate);
  const [endDate, setEndDate] = useState(initialValues?.endDate);
  const [reportDate, setReportDate] = useState(
    type === DURATION.SINGLE ? singleReportDate : new Date(),
  );
  const [duration, setDuration] = useState(durationOptions[type === DURATION.PAST ? 2 : 1]);
  const [dateRangeDisabled, setDateRangeDisabled] = useState(true);
  const [contractNumber, setContractNumber] = useState('');

  const debounceApiCall = useCallback(
    debounce((val) => {
      searchHandler(val);
    }, 2000),
    [],
  );
  const onSearchInputChange = (event) => {
    const { value } = event.target;
    setContractNumber(value);
    debounceApiCall(value);
  };

  const dateOptionsGenerator = (dateType) => {
    const dateOptions = {
      minDate: '',
      maxDate: '',
      dateFormat: dateFormat.M_D_Y,
    };
    switch (type) {
      case DURATION.PAST: {
        if (dateType === 'toDate') {
          dateOptions.minDate = startDate ? moment(startDate)._d : '';
          dateOptions.maxDate = moment().endOf('day')._d;
        } else dateOptions.maxDate = moment().endOf('day')._d;
        return dateOptions;
      }
      case DURATION.FUTURE: {
        if (dateType === 'toDate') {
          dateOptions.minDate = startDate ? moment(startDate)._d : '';
        } else dateOptions.minDate = moment().add(1, 'day').startOf('day')._d;
        return dateOptions;
      }
      default: {
        return options;
      }
    }
  };

  return (
    <div className="dashboard-duration-filters-wrapper">
      <div className="dashboard-contract-filter">
        <label>Contract Number</label>
        <SearchInput
          placeholder="Search..."
          searchText={contractNumber}
          handleChange={onSearchInputChange}
        />
      </div>
      {type === DURATION.SINGLE ? (
        <div className="dashboard-report-date">
          <DatePicker
            required
            formInput={false}
            label="Report Date"
            placeholder="Date"
            value={reportDate}
            options={options}
            kind="md"
            onChange={([selectedDate]) => {
              setReportDate(selectedDate);
              dateChangeHandler(selectedDate, null);
            }}
          />
        </div>
      ) : (
        <div className="duration-filter">
          <Select
            required
            formInput={false}
            options={durationOptions}
            value={duration}
            label="Duration"
            isSearchable={false}
            onChange={(option) => {
              setDuration(option);
              if (option.value !== 'custom') {
                const range = getRange(option.value);
                setDateRangeDisabled(true);
                setStartDate(moment(range.startDate).toDate());
                setEndDate(moment(range.endDate).toDate());
                dateChangeHandler(range, option);
              } else {
                setDateRangeDisabled(false);
                setStartDate(null);
                setEndDate(null);
              }
            }}
          />
          <DatePicker
            required
            formInput={false}
            placeholder="Date"
            label="From"
            value={startDate}
            options={dateOptionsGenerator('fromDate')}
            kind="md"
            onChange={([selectedDate]) => {
              setStartDate(selectedDate);
              if (type === DURATION.FUTURE) setEndDate(selectedDate);
              else setEndDate('');
            }}
            disabled={!duration?.value ? true : dateRangeDisabled}
          />
          <DatePicker
            required
            formInput={false}
            placeholder="Date"
            label="To"
            value={endDate}
            options={dateOptionsGenerator('toDate')}
            kind="md"
            onChange={([selectedDate]) => {
              setEndDate(selectedDate);
            }}
            disabled={!duration?.value || !startDate ? true : dateRangeDisabled}
          />
          <button
            type="button"
            disabled={!(startDate && endDate && duration?.value === 'custom')}
            className="generate-btn"
            onClick={() => {
              dateChangeHandler(
                {
                  startDate,
                  endDate,
                },
                duration,
              );
            }}
          >
            Generate
          </button>
        </div>
      )}
    </div>
  );
}

DurationFilters.propTypes = {
  type: PropTypes.string.isRequired,
  dateChangeHandler: PropTypes.func.isRequired,
  searchHandler: PropTypes.func.isRequired,
};

export default DurationFilters;
